import _ from 'lodash';
import clsx from 'clsx';
import Style from './notes.module.scss';
import { FC, useCallback, useContext, useMemo } from 'react';
import { AppButton, AppButtonProps } from 'uikit/buttons';
import { useTranslation } from 'react-i18next';
import { ReactComponent as EditIcon } from 'assets/img/common/edit_20.svg';
import { ReactComponent as ExportIcon } from 'assets/img/common/export.svg';
import { ReactComponent as Dots } from 'assets/img/common/dots_20.svg';
import { EditReasonEnum, FormDto, IFormResultDto, IFormResultVersionDto } from '../../../services/api/api-client';
import { noteBodyFormControlsResolver } from './formControls/body/noteBodyFormControlsResolver';
import { Editor, Element, Frame } from '@craftjs/core';
import { NoteContext, NoteContextType } from './NoteContext';
import { noteHeaderFormControlsResolver } from './formControls/header/noteHeaderFormControlsResolver';
import { EditReasonFormHeader } from 'src/features/forms/overview/headers/EditReasonFormHeader';
import { useFormEditing } from 'src/features/forms/editing/useFormEditing';
import { IssueMark } from 'src/components/issue/issueTarget/issueMark.component';
import { conditionalConcat } from '../../../helpers/arrayHelpers';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { DropDownMenu } from 'uikit/dropDownMenu/dropDownMenu.component';
import { DropDownMenuOption } from 'uikit/dropDownMenu/dropDownMenu';
import { useFormExport } from '../../../features/forms/overview/export/FormExport';
import { toDateOrNull } from '../../../helpers/date-helpers';
import { isFirstResultVersion } from 'src/features/forms/form-helper';

type HeaderButtonsType = AppButtonProps & {
  key: string;
  buttonTextDescription: string;
};

export const Note: FC<{
  formDto: FormDto;
  formResult: IFormResultDto;
  testId?: string;
  isOverview?: boolean;
  noteDateDataKey: string;
  patientUniqueId: string;
  canEdit: boolean;
}> = (props) => {
  const lastResultVersion = _.last(props.formResult.versions)!;
  const { t } = useTranslation();
  const formExporting = useFormExport({
    patientUniqId: props.patientUniqueId,
    stepName: '',
    formResultVersion: lastResultVersion,
    formConfig: props.formDto,
    customFormDate: toDateOrNull(lastResultVersion?.value?.[props.noteDateDataKey]),
  });

  const noteContextValue = useMemo<NoteContextType>(
    () => ({
      formResult: props.formResult,
      currentResultVersion: lastResultVersion,
      noteDateDataKey: props.noteDateDataKey,
      mode: props.isOverview ? 'overview' : 'section',
      patientUniqueId: props.patientUniqueId,
    }),
    [lastResultVersion, props.formResult, props.isOverview, props.noteDateDataKey, props.patientUniqueId],
  );

  const formEditing = useFormEditing();
  const onEditNote = useCallback(() => {
    formEditing.startFormEditing({ formResult: props.formResult, stepName: undefined });
  }, [formEditing, props.formResult]);

  const headerButtons: HeaderButtonsType[] = useMemo(
    () =>
      conditionalConcat<HeaderButtonsType>(
        props.canEdit && [
          {
            key: 'EditForm',
            onClick: onEditNote,
            variant: 'icon-link',
            colorSchema: 'primary',
            Icon: EditIcon,
            // @ts-ignore
            buttonTextDescription: t('Common_Edit'),
            testId: 'edit-notes-button',
          },
        ],
        formExporting.canExport && [
          {
            key: 'ExportDataLogs',
            onClick: formExporting.openExportModal,
            variant: 'icon-link',
            colorSchema: 'primary',
            Icon: ExportIcon,
            buttonTextDescription: t('Forms.Overview.Export.ExportButton'),
            testId: 'export-notes-button',
          },
        ],
      ),
    [formExporting.canExport, formExporting.openExportModal, props.canEdit, onEditNote, t],
  );

  return (
    <NoteContext.Provider value={noteContextValue}>
      <>
        <div className={clsx(Style.note, { [Style.noteOverview]: props.isOverview })} data-test-id={props.testId}>
          <NoteHeader
            form={props.formDto}
            formResult={lastResultVersion}
            actions={headerButtons}
            editReason={{
              editReason: lastResultVersion.editReason,
              editReasonText: lastResultVersion.updateReasonText,
            }}
          />
          <NoteBody form={props.formDto} />
        </div>
        {formEditing.element}
        {formExporting.Modal}
      </>
    </NoteContext.Provider>
  );
};

const NoteHeader: FC<{
  form: FormDto;
  formResult: IFormResultVersionDto;
  actions: HeaderButtonsType[];
  editReason?: {
    editReason: EditReasonEnum | null;
    editReasonText: string | null;
  };
}> = (props) => {
  const noteContext = useContext(NoteContext);
  const isOverview = noteContext.mode === 'overview';

  return (
    <div data-test-id={'note-header'} className={Style.noteHeader}>
      <div className={Style.noteHeaderLeftPart}>
        <IssueMark
          issueContext={{
            subject: 'Patient',
            linkedPatientUniqId: noteContext.patientUniqueId,
            topic: 'Notes',
            resultId: props.formResult.formResultId || undefined,
          }}
          countDescendants={false}
        >
          <div className={Style.statusIconsContainer}>
            <Editor resolver={noteHeaderFormControlsResolver}>
              <Frame data={props.form.layoutSchema}>
                <Element canvas={true} />
              </Frame>
            </Editor>
          </div>
        </IssueMark>
        <EditReasonFormHeader
          isUpdated={!isFirstResultVersion(props.formResult)}
          updatedAt={props.formResult?.updatedAt}
          editReason={isOverview ? props.editReason?.editReason : undefined}
          updateReasonText={isOverview ? props.editReason?.editReasonText : undefined}
          editorName={props.formResult?.doctor?.fullName}
          testId={'note-update-date'}
        />
      </div>
      <HeaderButtons controls={props.actions} />
    </div>
  );
};

const NoteBody: FC<{ form: FormDto }> = (props) => {
  const noteContext = useContext(NoteContext);
  const isOverview = noteContext.mode === 'overview';

  return (
    <Editor resolver={noteBodyFormControlsResolver}>
      <div data-test-id={'note-body'} className={clsx(Style.noteBody, { [Style.noteOverview]: isOverview })}>
        <Frame data={props.form.layoutSchema}>
          <Element canvas={true} />
        </Frame>
      </div>
    </Editor>
  );
};

const HeaderButtons: FC<{ controls: HeaderButtonsType[] }> = (props) => {
  const noteContext = useContext(NoteContext);
  const isOverview = noteContext.mode === 'overview';

  const menuOptions: DropDownMenuOption[] = useMemo(() => {
    return [
      ...props.controls.map(({ Icon, key, buttonTextDescription, onClick }) => {
        return {
          key: key,
          text: buttonTextDescription,
          action: onClick,
          icon: Icon ? <Icon /> : undefined,
        };
      }),
    ] as DropDownMenuOption[];
  }, [props.controls]);

  // if we have only one option for dropdown - use icon directly (as we do for 'overview' mode)
  if (isOverview || menuOptions.length < 2) {
    return (
      <div className={Style.noteHeaderActions}>
        {props.controls.map(({ buttonTextDescription, ...x }) => (
          <Tooltip key={x.key} text={buttonTextDescription}>
            <AppButton {...x} />
          </Tooltip>
        ))}
      </div>
    );
  }

  return <DropDownMenu colorSchema={'basic'} Icon={Dots} options={menuOptions} testId={'note-actions-menu'} />;
};
